import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

// ──────────────────────────────────────────────
// RoleGuard — Checks user permissions and shows 403 or redirects
// if the user doesn't have the required permissions.
// ──────────────────────────────────────────────

interface RoleGuardProps {
  /** Permissions required to access the wrapped content (OR logic) */
  requiredPermissions: string[];
  children: React.ReactNode;
}

function ForbiddenPage() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4">
      <div className="text-center">
        <p className="text-6xl font-bold text-slate-200">403</p>
        <h1 className="mt-4 text-xl font-semibold text-slate-900">
          Acceso denegado
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          No tienes permisos para acceder a esta seccion.
          Contacta con el administrador de tu organizacion.
        </p>
        <a
          href="/"
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700"
        >
          Volver al inicio
        </a>
      </div>
    </div>
  );
}

function RoleGuard({ requiredPermissions, children }: RoleGuardProps) {
  const { isAuthenticated, permissions } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Wildcard grants all access (owner)
  if (permissions.includes('*')) {
    return <>{children}</>;
  }

  // Check if user has at least one required permission
  const hasAccess = requiredPermissions.some((p) => permissions.includes(p));

  if (!hasAccess) {
    return <ForbiddenPage />;
  }

  return <>{children}</>;
}

RoleGuard.displayName = 'RoleGuard';

export { RoleGuard };
